import { makeAutoObservable, runInAction } from 'mobx'
import { RootStore } from 'stores'
import { PlayerProfile, PlayerProfileUpdate, PlayerMetric, CreateMetricPayload } from 'types/player'
import { getPlayerProfile, updatePlayerProfile, getPlayerMetrics, addPlayerMetric } from 'API/player'

export default class PlayerProfileStore {
  rootStore: RootStore

  profile: PlayerProfile | null = null
  metrics: PlayerMetric[] = []
  isLoading = false
  error: string | null = null

  constructor(rootStore: RootStore) {
    makeAutoObservable(this)
    this.rootStore = rootStore
  }

  /** Load player profile for a given user */
  async loadProfile(userId: number): Promise<void> {
    runInAction(() => {
      this.isLoading = true
      this.error = null
    })
    try {
      const profile = await getPlayerProfile(userId)
      runInAction(() => {
        this.profile = profile
      })
    } catch (err: unknown) {
      runInAction(() => {
        this.error = err instanceof Error ? err.message : 'Failed to load profile'
      })
    } finally {
      runInAction(() => {
        this.isLoading = false
      })
    }
  }

  /** Update own player profile */
  async saveProfile(userId: number, data: PlayerProfileUpdate): Promise<PlayerProfile | null> {
    runInAction(() => {
      this.isLoading = true
      this.error = null
    })
    try {
      const updated = await updatePlayerProfile(userId, data)
      runInAction(() => {
        this.profile = updated
      })
      return updated
    } catch (err: unknown) {
      runInAction(() => {
        this.error = err instanceof Error ? err.message : 'Failed to update profile'
      })
      return null
    } finally {
      runInAction(() => {
        this.isLoading = false
      })
    }
  }

  /** Load metrics for a given user */
  async loadMetrics(userId: number): Promise<void> {
    try {
      const metrics = await getPlayerMetrics(userId)
      runInAction(() => {
        this.metrics = metrics
      })
    } catch (err: unknown) {
      runInAction(() => {
        this.error = err instanceof Error ? err.message : 'Failed to load metrics'
      })
    }
  }

  /** Add a new self-entry metric */
  async createMetric(userId: number, data: CreateMetricPayload): Promise<PlayerMetric | null> {
    try {
      const metric = await addPlayerMetric(userId, data)
      runInAction(() => {
        this.metrics.push(metric)
      })
      return metric
    } catch (err: unknown) {
      runInAction(() => {
        this.error = err instanceof Error ? err.message : 'Failed to add metric'
      })
      return null
    }
  }

  /** Reset store state */
  reset(): void {
    this.profile = null
    this.metrics = []
    this.isLoading = false
    this.error = null
  }
}
